import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createOrUpdateHPoolAction,
  setAssetSigantures as _setAssetSigantures,
} from '../redux/actions/createHPoolActions';
import { createNotification } from '../redux/actions/uiActions';
import {
  defaultSignedType,
  getAssetSignatureRequest,
  mapOrderType,
  handleModifiedOrders,
  updateTradeOrder,
  parseResponseSignature,
} from '../services/hpoolService';
import { constructMiniObj, multiConver, objectMapper } from '../utils/mapper';
import { useEthConverter } from './useConverter';
import { usePromise } from './usePromise/usePromise';

export const useAssetsSignatures = (signatureType = defaultSignedType) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [updateReq, , updateLoading] = usePromise(updateTradeOrder, { method: 'PUT', showError: true });

  const startLoading = (tokenId) => {
    // tokenId: string => current signed token
    setLoading(tokenId);
  };
  const stopLoading = () => {
    setLoading(false);
  };
  const {
    assetSignatures,
    tradeOrders = [],
    hpool: { id: hPoolId },
  } = useSelector((state) => state.createHPool);

  const signedTxsIds = useMemo(
    () => tradeOrders.filter((tradeOrder) => tradeOrder.is_signed)?.map((tradeOrder) => tradeOrder.token_id),
    [tradeOrders],
  );

  const unSignedTxs = useMemo(() => tradeOrders.filter((tradeOrder) => !tradeOrder.is_signed), [tradeOrders]);

  const handleHpoolUpdate = (assets = {}, info = {}) => {
    // send draft_finished on the last tx
    try {
      const updatedAssetSignatures = { asset_signatures: assets, id: hPoolId, ...info };
      dispatch(createOrUpdateHPoolAction(updatedAssetSignatures)).finally(stopLoading);
    } catch (signingError) {
      console.log({ signingError });
    }
  };

  const onSignSuccess = (tokenId, assetSignature, info = {}) => {
    try {
      const newAssets = { ...assetSignatures, [tokenId]: assetSignature };
      dispatch(_setAssetSigantures(newAssets));
      handleHpoolUpdate(newAssets, info);
    } catch (error) {
      console.log({ updatePoolError: error });
    }
  };
  const handleError = (error = {}) => {
    dispatch(createNotification('error', error.message, 4000));
    stopLoading();
  };

  const setAssetSigantures = async (asset) => {
    try {
      const { token_id: tokenId } = asset || {};
      startLoading(tokenId);
      const assetSignature = await getAssetSignatureRequest({ ...asset }, signatureType);
      const isLastTx = unSignedTxs.length === 1;
      const draftFinished = (!!isLastTx && { draft_finished: true }) || {};
      onSignSuccess(tokenId, assetSignature, draftFinished);
    } catch (error) {
      handleError(error);
    }
  };
  const setModifiedOrderSignature = async (asset) => {
    try {
      const { token_id: tokenId } = asset || {};
      startLoading(tokenId);
      const assetSignature = await handleModifiedOrders(asset, signatureType);
      onSignSuccess(tokenId, assetSignature);
    } catch (error) {
      handleError(error);
    }
  };

  const constructFutureOrderPayload = (tradeOrder) => {
    const keys = ['amount_usd', 'price_usd', 'valid_until', 'token_id', 'hpool_id', 'order_type'];
    let _reqBody;
    _reqBody = constructMiniObj(keys, tradeOrder);
    _reqBody = objectMapper(_reqBody, { order_type: 'type' });
    _reqBody = multiConver(
      [
        { keys: ['type'], convert: (value) => value?.toLowerCase() },
        { keys: ['valid_until'], convert: (value) => new Date(value).getTime() },
      ],
      _reqBody,
    );
    return _reqBody;
  };

  const updateFutureOrder = (tradeOrder = {}) =>
    new Promise(async (res, rej) => {
      try {
        // here we're using trade order id instead of token_id cuz all trade orders here has the same token_id and we need unique id for loading
        startLoading(tradeOrder.id);

        const payload = { trade_order: tradeOrder };
        const _reqBody = constructFutureOrderPayload(tradeOrder);
        const { id: tradeOrderId } = tradeOrder;
        const signature = await parseResponseSignature(payload);
        const reqBody = {
          ..._reqBody,
          signature,
          slippage: 2.5,
        };
        updateReq(tradeOrderId, reqBody)
          .then(() => res(tradeOrder))
          .catch(handleError)
          .finally(stopLoading);
      } catch (error) {
        handleError(error);
        console.log('');
      }
    });

  const isInAssetSignature = (tokenId) => signedTxsIds.find((signedTx) => +signedTx === +tokenId);

  return [
    assetSignatures,
    setAssetSigantures,
    isInAssetSignature,
    setModifiedOrderSignature,
    loading,
    unSignedTxs,
    updateFutureOrder,
  ];
};

export const updateFutureOrderIndex = 6;
export const loadingIndex = 4;
